import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { startTraining, getTrainingStatus, deleteModel } from '../../lib/callables';

interface Model {
  id: string;
  snapshotIds: string[];
  modelName?: string;
  artifactUri: string | null;
  metrics: any | null;
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'prod' | 'archived';
  createdAt: any;
  createdBy: string;
  trainConfig: {
    base: string;
    epochs: number;
    imgSize: number;
    lr: number;
  };
}

interface Task {
  id: string;
  title: string;
  category: string;
}

interface Snapshot {
  id: string;
  taskId: string;
  name?: string;
  counts: {
    train: number;
    val: number;
    test: number;
    positives: number;
    negatives: number;
  };
  classes: string[];
  createdAt: any;
}

const ModelsPage: React.FC = () => {
  const [models, setModels] = useState<Model[]>([]);
  const [tasks, setTasks] = useState<Record<string, Task>>({});
  const [snapshots, setSnapshots] = useState<Record<string, Snapshot>>({});
  const [loading, setLoading] = useState(true);
  const [showTrainModal, setShowTrainModal] = useState(false);
  const [trainingConfig, setTrainingConfig] = useState({
    snapshotIds: [] as string[],
    modelName: '',
    base: 'yolov8n-cls.pt' as 'yolov8n-cls.pt' | 'yolov8s-cls.pt',
    epochs: 20,
    imgSize: 320,
    lr: 0.001
  });
  const [training, setTraining] = useState(false);
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  // Load models, tasks, and snapshots
  useEffect(() => {
    let unsubscribeModels: (() => void) | undefined;
    let unsubscribeTasks: (() => void) | undefined;
    let unsubscribeSnapshots: (() => void) | undefined;

    try {
      // Load models with error handling (without orderBy to avoid index issues)
      unsubscribeModels = onSnapshot(
        collection(db, 'models'),
        (snapshot) => {
          const modelsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as Model[];
          // Sort by createdAt in JavaScript instead of Firestore
          modelsData.sort((a, b) => {
            const aTime = a.createdAt?.toDate?.()?.getTime() || 0;
            const bTime = b.createdAt?.toDate?.()?.getTime() || 0;
            return bTime - aTime; // Descending order
          });
          setModels(modelsData);
        },
        (error) => {
          console.error('Error loading models:', error);
          setModels([]);
        }
      );

      // Load tasks with error handling
      unsubscribeTasks = onSnapshot(
        collection(db, 'tasks'),
        (snapshot) => {
          const tasksData: Record<string, Task> = {};
          snapshot.docs.forEach(doc => {
            tasksData[doc.id] = { id: doc.id, ...doc.data() } as Task;
          });
          setTasks(tasksData);
        },
        (error) => {
          console.error('Error loading tasks:', error);
          setTasks({});
        }
      );

      // Load snapshots with error handling
      unsubscribeSnapshots = onSnapshot(
        collection(db, 'dataset_snapshots'),
        (snapshot) => {
          const snapshotsData: Record<string, Snapshot> = {};
          snapshot.docs.forEach(doc => {
            snapshotsData[doc.id] = { id: doc.id, ...doc.data() } as Snapshot;
          });
          setSnapshots(snapshotsData);
        },
        (error) => {
          console.error('Error loading snapshots:', error);
          setSnapshots({});
        }
      );
    } catch (error) {
      console.error('Error setting up listeners:', error);
    }

    setLoading(false);

    return () => {
      if (unsubscribeModels) unsubscribeModels();
      if (unsubscribeTasks) unsubscribeTasks();
      if (unsubscribeSnapshots) unsubscribeSnapshots();
    };
  }, []);

  // Poll training status for active models every 10 seconds
  useEffect(() => {
    const activeModels = models.filter(m => m.status === 'queued' || m.status === 'running');
    if (activeModels.length === 0) return;

    const interval = setInterval(async () => {
      for (const model of activeModels) {
        try {
          const result = await getTrainingStatus({ modelId: model.id });
          // Firestore listener will pick up the update automatically
          // Just log for visibility
          console.log(`[Polling] Model ${model.id}: ${result.status}`);
          if (result.status === 'succeeded' || result.status === 'failed') {
            console.log(`[Polling] Model ${model.id} finished with status: ${result.status}`);
          }
        } catch (err) {
          console.error(`[Polling] Error checking status for ${model.id}:`, err);
        }
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [models]);

  const handleStartTraining = async () => {
    if (trainingConfig.snapshotIds.length === 0) {
      alert('Please select at least one snapshot');
      return;
    }

    setTraining(true);
    try {
      const result = await startTraining({
        snapshotIds: trainingConfig.snapshotIds,
        modelName: trainingConfig.modelName || `Model ${new Date().toLocaleDateString()}`,
        base: trainingConfig.base,
        epochs: trainingConfig.epochs,
        imgSize: trainingConfig.imgSize,
        lr: trainingConfig.lr
      });
      alert(`Training started! Model ID: ${result.modelId}`);
      setShowTrainModal(false);
      setTrainingConfig({
        snapshotIds: [],
        modelName: '',
        base: 'yolov8n-cls.pt',
        epochs: 20,
        imgSize: 320,
        lr: 0.001
      });
    } catch (error) {
      console.error('Error starting training:', error);
      alert('Failed to start training');
    } finally {
      setTraining(false);
    }
  };

  const handleDeleteModel = async (modelId: string) => {
    if (!window.confirm('Are you sure you want to permanently delete this model? This cannot be undone.')) {
      return;
    }
    try {
      await deleteModel({ modelId });
      alert('Model deleted');
      setShowDetails(false);
    } catch (error) {
      console.error('Error deleting model:', error);
      alert('Failed to delete model');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'queued': return 'bg-yellow-100 text-yellow-800';
      case 'running': return 'bg-blue-100 text-blue-800';
      case 'succeeded': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'prod': return 'bg-purple-100 text-purple-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getOverallF1 = (metrics: any) => {
    if (!metrics || !metrics.overall) return 'N/A';
    return (metrics.overall.f1 * 100).toFixed(1) + '%';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading models...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Models</h1>
          <p className="text-gray-600">Train and manage ML models for auto-approval</p>
        </div>
        <button
          onClick={() => setShowTrainModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Train Model
        </button>
      </div>

      {/* Models Table */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Trained Models</h3>
          <p className="text-sm text-gray-500">Manage your trained models and their status</p>
        </div>

        {models.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">🤖</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Models Yet</h3>
            <p className="text-gray-500 mb-4">
              Train your first model to get started with auto-approval.
            </p>
            <p className="text-sm text-gray-400 mb-4">
              Note: You'll need to create a dataset snapshot first before training a model.
            </p>
            <button
              onClick={() => setShowTrainModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Train Model
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Model ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Snapshots
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Overall F1
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {models.map((model) => (
                  <tr key={model.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {model.id.substring(0, 12)}...
                      </div>
                      <div className="text-sm text-gray-500">
                        {model.trainConfig.base}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {model.modelName || '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {model.snapshotIds?.length || 0} snapshot{model.snapshotIds?.length !== 1 ? 's' : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(model.status)}`}>
                          {model.status}
                        </span>
                        {(model.status === 'queued' || model.status === 'running') && (
                          <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                          </svg>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getOverallF1(model.metrics)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {model.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setSelectedModel(model);
                            setShowDetails(true);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Details
                        </button>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(model.id);
                            alert('Model ID copied to clipboard!');
                          }}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          Copy ID
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Train Model Modal */}
      {showTrainModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Train New Model</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Model Name
                  </label>
                  <input
                    type="text"
                    value={trainingConfig.modelName}
                    onChange={(e) => setTrainingConfig(prev => ({ ...prev, modelName: e.target.value }))}
                    placeholder="e.g. Handwriting v1"
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Snapshots <span className="text-gray-400 font-normal">(select one or more)</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        const allIds = Object.values(snapshots).map(s => s.id);
                        const allSelected = allIds.every(id => trainingConfig.snapshotIds.includes(id));
                        setTrainingConfig(prev => ({
                          ...prev,
                          snapshotIds: allSelected ? [] : allIds
                        }));
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      {Object.values(snapshots).every(s => trainingConfig.snapshotIds.includes(s.id))
                        ? 'Deselect All'
                        : 'Select All'}
                    </button>
                  </div>
                  <div className="border border-gray-300 rounded max-h-48 overflow-y-auto">
                    {Object.values(snapshots).length === 0 ? (
                      <div className="p-3 text-sm text-gray-500">No snapshots available. Create a snapshot first.</div>
                    ) : (
                      Object.values(snapshots).map(snapshot => {
                        const isSelected = trainingConfig.snapshotIds.includes(snapshot.id);
                        return (
                          <div
                            key={snapshot.id}
                            onClick={() => {
                              setTrainingConfig(prev => ({
                                ...prev,
                                snapshotIds: isSelected
                                  ? prev.snapshotIds.filter(id => id !== snapshot.id)
                                  : [...prev.snapshotIds, snapshot.id]
                              }));
                            }}
                            className={`flex items-center p-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${isSelected ? 'bg-blue-50' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {}}
                              className="mr-3"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {snapshot.name || snapshot.id.substring(0, 16) + '...'}
                              </div>
                              <div className="text-xs text-gray-500">
                                {snapshot.classes.filter(c => c !== 'Other').join(', ')} • {snapshot.counts.train} train / {snapshot.counts.val} val
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  {trainingConfig.snapshotIds.length > 0 && (
                    <div className="mt-1 text-xs text-blue-600">
                      {trainingConfig.snapshotIds.length} snapshot{trainingConfig.snapshotIds.length !== 1 ? 's' : ''} selected
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Base Model
                    </label>
                    <select
                      value={trainingConfig.base}
                      onChange={(e) => setTrainingConfig(prev => ({
                        ...prev,
                        base: e.target.value as 'yolov8n-cls.pt' | 'yolov8s-cls.pt'
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="yolov8n-cls.pt">YOLOv8n-cls (nano)</option>
                      <option value="yolov8s-cls.pt">YOLOv8s-cls (small)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Epochs
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={trainingConfig.epochs}
                      onChange={(e) => setTrainingConfig(prev => ({
                        ...prev,
                        epochs: parseInt(e.target.value)
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Image Size
                    </label>
                    <input
                      type="number"
                      min="224"
                      max="640"
                      step="32"
                      value={trainingConfig.imgSize}
                      onChange={(e) => setTrainingConfig(prev => ({
                        ...prev,
                        imgSize: parseInt(e.target.value)
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Learning Rate
                    </label>
                    <input
                      type="number"
                      min="0.0001"
                      max="0.1"
                      step="0.0001"
                      value={trainingConfig.lr}
                      onChange={(e) => setTrainingConfig(prev => ({
                        ...prev,
                        lr: parseFloat(e.target.value)
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowTrainModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStartTraining}
                  disabled={training || trainingConfig.snapshotIds.length === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {training ? 'Starting...' : 'Start Training'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Model Details Modal */}
      {showDetails && selectedModel && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Model Details</h3>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Model ID</label>
                    <p className="text-sm text-gray-900">{selectedModel.id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedModel.status)}`}>
                      {selectedModel.status}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <p className="text-sm text-gray-900">{selectedModel.modelName || '—'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Snapshots</label>
                  {selectedModel.snapshotIds?.length ? (
                    <ul className="text-sm text-gray-900 list-disc list-inside">
                      {selectedModel.snapshotIds.map(id => (
                        <li key={id}>
                          {snapshots[id]?.name || id}
                          {snapshots[id] && (
                            <span className="text-gray-500">
                              {' '}({snapshots[id].counts.train} train / {snapshots[id].counts.val} val)
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-900">—</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Training Configuration</label>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-sm">Base: {selectedModel.trainConfig.base}</p>
                    <p className="text-sm">Epochs: {selectedModel.trainConfig.epochs}</p>
                    <p className="text-sm">Image Size: {selectedModel.trainConfig.imgSize}</p>
                    <p className="text-sm">Learning Rate: {selectedModel.trainConfig.lr}</p>
                  </div>
                </div>

                {selectedModel.metrics && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Metrics</label>
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-sm">Overall F1: {getOverallF1(selectedModel.metrics)}</p>
                      {selectedModel.metrics.perClass && (
                        <div className="mt-2">
                          <p className="text-sm font-medium">Per-Class Metrics:</p>
                          {Object.entries(selectedModel.metrics.perClass).map(([className, metrics]: [string, any]) => (
                            <div key={className} className="ml-4 text-sm">
                              <p>{className}: F1={((metrics.f1 || 0) * 100).toFixed(1)}%</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedModel.artifactUri && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Artifact URI</label>
                    <p className="text-sm text-gray-900 break-all">{selectedModel.artifactUri}</p>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(selectedModel.artifactUri!);
                        alert('Artifact URI copied to clipboard!');
                      }}
                      className="mt-1 text-blue-600 hover:text-blue-900 text-sm"
                    >
                      Copy URI
                    </button>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowDetails(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Close
                </button>
                <button
                  onClick={() => handleDeleteModel(selectedModel.id)}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                >
                  Delete Model
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelsPage;

